// Общие для модулей cycle/ типы, форматтеры и мелкие презентационные
// компоненты. Вынесено из CycleTab.tsx без изменения поведения
// (Фаза 1, разбор монолита). Точка входа CycleTab остаётся в CycleTab.tsx.

import { useState, useEffect } from "react";
import {
  api,
  type CampaignAvatar,
  type CampaignSetup,
  type CyclePhase,
  type GameCycle,
  type Initiative,
  type InitiativeRiskLevel,
  type InitiativeVoteStance,
  type MediaAsset,
  type NpcVisit,
  type ChronicleEvent,
  type ChronicleChapter,
  type CycleAction,
  type WorldChange,
  type ActionNarrativeStatus,
} from "../api";
import { splitNarrativeParagraphs } from "../text";

const GAME_UI_ASSET_BASE = "/assets/game-ui";

interface Props {
  campaignId: string;
  setup: CampaignSetup;
  action: (label: string, run: () => Promise<unknown>) => Promise<boolean>;
  activeSection?: CycleSection;
  onSectionChange?: (section: CycleSection) => void;
  onOpenTab?: (tab: "chronicle" | "world" | "council" | "dossier") => void;
}

// cycleNavigationFor() (in CycleTab.tsx) only ever returns these four tabs, so these
// are the only reachable values -- see docs/DEVELOPMENT_AUDIT_2026_07_26.md
// for the dead-code removal this narrowing is part of.
export type CycleSection = "situation" | "actions" | "chronicle" | "hq";
type Section = CycleSection;

type ActionWorkspaceTab = "actions" | "chats" | "initiatives";

const KNOWN_CYCLE_SECTIONS: CycleSection[] = [
  "situation",
  "actions",
  "chronicle",
  "hq",
];

function isKnownCycleSection(value: string): value is CycleSection {
  return KNOWN_CYCLE_SECTIONS.includes(value as CycleSection);
}

function OwnerForcePhaseControl({ campaignId, setup, action }: Props) {
  const cycle = setup.cycle.current;
  if (
    !setup.canManage ||
    !cycle?.canForceCompletePhase ||
    !cycle.forceCompletePhaseLabel
  )
    return null;
  const help = forcePhaseHelp(cycle.phase);
  return (
    <section
      className="cc-owner-force-card"
      aria-label="Управление этапом организатора"
    >
      <div>
        <strong>{cycle.forceCompletePhaseLabel}</strong>
        <p>{help}</p>
      </div>
      <button
        type="button"
        onClick={() =>
          void action(`${cycle.forceCompletePhaseLabel}…`, () =>
            api.forceCompleteCyclePhase(campaignId),
          )
        }
      >
        {cycle.forceCompletePhaseLabel}
      </button>
    </section>
  );
}

function forcePhaseHelp(phase: CyclePhase): string {
  switch (phase) {
    case "DAY":
      return "Закроет этап действий для всех участников. Незавершённые совместные действия будут отменены, а неиспользованные действия сгорят.";
    case "DAY_CLOSING":
      return "Закроет последние NPC-встречи и переведёт цикл к подготовке инициатив.";
    case "AGENDA":
      return "Зафиксирует текущие инициативы и откроет голосование, даже если не все игроки отметили готовность.";
    case "VOTING":
      return "Засчитает отсутствующие голоса как воздержание и сразу запустит расчёт последствий.";
    default:
      return "Организатор может досрочно закрыть текущий этап.";
  }
}

function GameAssetIcon({ src, fallback }: { src: string; fallback: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <b>{fallback}</b>;
  return <img src={src} alt="" onError={() => setFailed(true)} />;
}

function compactText(value: string | undefined, maxLength = 120): string {
  const normalized = (value ?? "").replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trim()}…`;
}

function PlayerIdentity({
  campaignId,
  memberId,
  name,
  avatar,
}: {
  campaignId: string;
  memberId: string;
  name: string;
  avatar: CampaignAvatar;
}) {
  return (
    <div className="cc-player-identity">
      <Avatar
        campaignId={campaignId}
        memberId={memberId}
        name={name}
        avatar={avatar}
      />
      <span>{name}</span>
    </div>
  );
}

function Avatar({
  campaignId,
  memberId,
  name,
  avatar,
}: {
  campaignId: string;
  memberId: string;
  name: string;
  avatar: CampaignAvatar;
}) {
  const [customUrl, setCustomUrl] = useState<string | null>(null);
  useEffect(() => {
    if (avatar.kind !== "CUSTOM") {
      setCustomUrl(null);
      return;
    }
    let cancelled = false;
    let url: string | null = null;
    void api
      .campaignAvatarContent(campaignId, memberId)
      .then((blob) => {
        if (!cancelled) {
          url = URL.createObjectURL(blob);
          setCustomUrl(url);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [campaignId, memberId, avatar.kind, avatar.updatedAt]);
  const src = avatar.kind === "TELEGRAM" ? avatar.telegramUrl : customUrl;
  return src ? (
    <img className="cc-mini-avatar" src={src} alt="" />
  ) : (
    <span className="cc-mini-avatar initials">{initials(name)}</span>
  );
}

function CycleImages({
  campaignId,
  assets,
  label,
  canManage,
  action,
}: {
  campaignId: string;
  assets: MediaAsset[];
  label: string;
  canManage: boolean;
  action: Props["action"];
}) {
  if (assets.length === 0)
    return (
      <div className="cc-cycle-image-progress">
        <span className="cc-spinner small" />
        <div>
          <strong>{label}</strong>
          <small>Задание создаётся</small>
        </div>
      </div>
    );
  return (
    <div className="cc-cycle-gallery">
      {assets.map((asset) => (
        <CycleImage
          campaignId={campaignId}
          asset={asset}
          canManage={canManage}
          action={action}
          key={asset.id}
        />
      ))}
    </div>
  );
}

function CycleImage({
  campaignId,
  asset,
  canManage,
  action,
}: {
  campaignId: string;
  asset: MediaAsset;
  canManage: boolean;
  action: Props["action"];
}) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (asset.status !== "READY") {
      setSrc(null);
      return;
    }
    let cancelled = false;
    let url: string | null = null;
    void api
      .cycleImageContent(campaignId, asset.id)
      .then((blob) => {
        if (!cancelled) {
          url = URL.createObjectURL(blob);
          setSrc(url);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [asset.id, asset.status, campaignId]);
  if (src)
    return (
      <figure className="cc-cycle-image-card">
        <img src={src} alt="Иллюстрация события" />
        {canManage && (
          <button
            type="button"
            className="cc-image-regenerate"
            onClick={() =>
              void action("Создаём новую версию иллюстрации…", () =>
                api.regenerateCycleImage(campaignId, asset.id),
              )
            }
          >
            Перегенерировать иллюстрацию
          </button>
        )}
      </figure>
    );
  return (
    <div
      className={`cc-cycle-image-progress${asset.status === "FAILED" ? " failed" : ""}`}
    >
      <span className={asset.status === "FAILED" ? "" : "cc-spinner small"}>
        {asset.status === "FAILED" ? "!" : ""}
      </span>
      <div>
        <strong>
          {asset.status === "FAILED"
            ? "Иллюстрация не создана"
            : "Создаём иллюстрацию"}
        </strong>
        <small>
          {asset.status === "FAILED"
            ? "История и игровые последствия уже доступны"
            : "Она появится автоматически"}
        </small>
        {asset.status === "FAILED" && canManage && (
          <button
            type="button"
            className="cc-image-regenerate"
            onClick={() =>
              void action("Пробуем создать иллюстрацию снова…", () =>
                api.regenerateCycleImage(campaignId, asset.id),
              )
            }
          >
            Повторить создание
          </button>
        )}
      </div>
    </div>
  );
}

function DeadlineBadge({ cycle }: { cycle: GameCycle }) {
  const value =
    cycle.phase === "DAY"
      ? cycle.dayDeadlineAt
      : cycle.phase === "DAY_CLOSING"
        ? cycle.closingDeadlineAt
        : cycle.phase === "VOTING"
          ? cycle.votingDeadlineAt
          : undefined;
  if (!value) return null;
  return (
    <DeadlineValue
      label={
        cycle.phase === "DAY"
          ? "день"
          : cycle.phase === "DAY_CLOSING"
            ? "окно встреч"
            : "голосование"
      }
      value={value}
    />
  );
}

function DeadlineValue({ label, value }: { label: string; value: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  const remaining = new Date(value).getTime() - now;
  const text = remaining <= 0 ? "срок истёк" : formatDuration(remaining);
  return (
    <span className={`cc-deadline${remaining <= 0 ? " expired" : ""}`}>
      <b>{text}</b>
      <small>{label}</small>
    </span>
  );
}

function formatDuration(milliseconds: number) {
  const totalMinutes = Math.max(1, Math.ceil(milliseconds / 60_000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days} д ${hours} ч`;
  if (hours > 0) return `${hours} ч ${minutes} мин`;
  return `${minutes} мин`;
}

function SectionTitle({
  icon,
  eyebrow,
  title,
}: {
  icon: string;
  eyebrow: string;
  title: string;
}) {
  const iconSrc = genericUiIconSrc(icon);
  return (
    <div className="cc-section-title">
      <span className="cc-section-title-icon">
        {iconSrc ? <GameAssetIcon src={iconSrc} fallback={icon} /> : icon}
      </span>
      <div>
        <small>{eyebrow}</small>
        <h2>{title}</h2>
      </div>
    </div>
  );
}

function genericUiIconSrc(icon: string): string | undefined {
  const file = {
    "⚠": "icon_ui_warning.png",
    "⌛": "icon_ui_time.png",
    "⏱": "icon_ui_time.png",
    "✓": "icon_ui_success.png",
    "✕": "icon_ui_failure.png",
    "×": "icon_ui_failure.png",
    "⚖": "icon_vote.png",
    "📖": "icon_room_chronicle.png",
    "↕": "icon_chronicle_world_change.png",
    "🚪": "icon_room_npc.png",
    "◆": "action_type_free.png",
    "◇": "icon_chronicle_not_funded.png",
    "✦": "icon_chronicle_initiative_resolved.png",
    "◉": "icon_ui_status.png",
    "☵": "icon_room_chats.png",
    "☷": "icon_room_council.png",
    "✎": "icon_create_campaign.png",
    "↳": "icon_chronicle_deferred.png",
    "⧉": "icon_chronicle_initiatives.png",
    "🤝": "icon_chronicle_agreement.png",
    "👤": "icon_chronicle_member_joined.png",
  }[icon];
  return file ? `${GAME_UI_ASSET_BASE}/${file}` : undefined;
}

function previewText(text: string | undefined, maxLength = 420): string {
  const normalized = splitNarrativeParagraphs(text ?? "").join(" ").replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  const slice = normalized.slice(0, maxLength);
  const safeEnd = Math.max(slice.lastIndexOf(". "), slice.lastIndexOf("! "), slice.lastIndexOf("? "));
  return `${(safeEnd > 180 ? slice.slice(0, safeEnd + 1) : slice).trim()}…`;
}

function Paragraphs({ text }: { text?: string }) {
  const paragraphs = splitNarrativeParagraphs(text ?? "");
  if (paragraphs.length === 0) return null;
  return (
    <div className="paragraph-text narrative-copy">
      {paragraphs.map((item, index) => (
        <p key={`${index}-${item.slice(0, 16)}`}>{item}</p>
      ))}
    </div>
  );
}

function npcVisitDeadlinePassed(visit: NpcVisit): boolean {
  return Boolean(
    visit.status === "ACTIVE" &&
    visit.expiresAt &&
    new Date(visit.expiresAt).getTime() <= Date.now(),
  );
}

function dayProgress(cycle: GameCycle): {
  used: number;
  total: number;
  percent: number;
} {
  const used = cycle.memberProgress.reduce(
    (sum, item) => sum + item.actionsUsed,
    0,
  );
  const total = cycle.memberProgress.reduce(
    (sum, item) => sum + item.actionLimit,
    0,
  );
  return {
    used,
    total,
    percent: total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0,
  };
}

function sameIds(left: string[], right: string[]) {
  if (left.length !== right.length) return false;
  const a = [...left].sort();
  const b = [...right].sort();
  return a.every((value, index) => value === b[index]);
}

function readDraft<T>(key: string, fallback: T): T {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
}

function writeDraft(key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* optional */
  }
}

function clearDraft(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* optional */
  }
}

function phaseInfo(phase: GameCycle["phase"]) {
  return {
    PREPARING: { label: "Подготовка", progress: 15 },
    DAY: { label: "День действий и переговоров", progress: 35 },
    DAY_CLOSING: { label: "Последнее окно встреч", progress: 48 },
    AGENDA: { label: "Этап инициатив", progress: 58 },
    VOTING: { label: "Голосование Совета", progress: 70 },
    RESOLVING: {
      label: "Формирование портфеля и расчёт последствий",
      progress: 82,
    },
    NARRATING: { label: "Создание хроники", progress: 94 },
    PUBLISHED: { label: "Цикл завершён", progress: 100 },
  }[phase];
}

function riskLabel(value: InitiativeRiskLevel) {
  return value === "LOW"
    ? "Низкий риск"
    : value === "HIGH"
      ? "Высокий риск"
      : "Средний риск";
}

function factionStanceLabel(
  value: Initiative["assessment"]["factions"][number]["stance"],
) {
  return value === "SUPPORTIVE"
    ? "поддержка"
    : value === "RESISTANT"
      ? "сопротивление"
      : "затронута";
}

function statusLabel(value: Initiative["status"]) {
  return {
    DRAFT: "Черновик",
    DISCUSSION: "Обсуждается",
    VOTING: "Голосование",
    ACCEPTED: "Принята и профинансирована",
    REJECTED: "Отклонена",
    NOT_FUNDED: "Нет финансирования",
    RESOLVED: "Реализована",
    MERGED: "Вошла в объединённую",
  }[value];
}

function fundingLabel(value: Initiative["fundingStatus"]) {
  return value === "FUNDED"
    ? "Финансирование зарезервировано"
    : value === "NOT_FUNDED"
      ? "Не вошла в доступный бюджет"
      : value === "NOT_APPLICABLE"
        ? "Финансирование не требуется"
        : "Бюджет ещё не распределён";
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function voteLabel(value: InitiativeVoteStance) {
  return value === "SUPPORT"
    ? "Поддержать"
    : value === "OPPOSE"
      ? "Возразить"
      : "Воздержаться";
}

function formatTime(value: string) {
  return new Date(value).toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "?"
  );
}

// --- lift-set: общие символы, поднятые для разрыва циклов между cycle/-модулями ---
function compactChangeLabel(value: string): string {
  return value.replace(/^Ослабление основы\s*/, "").replace(/[«»]/g, "").trim();
}
function MockupSectionTitle({
  eyebrow,
  title,
}: {
  eyebrow: string;
  title?: string;
}) {
  return (
    <header className="cc-mockup-section-title">
      <small>{eyebrow}</small>
      {title && <h3>{title}</h3>}
    </header>
  );
}
type ChronicleFeedItem =
  | {
      kind: "event";
      id: string;
      createdAt: string;
      event: ChronicleEvent;
      sourceAction?: CycleAction;
    }
  | {
      kind: "chapter";
      id: string;
      createdAt: string;
      chapter: ChronicleChapter;
      recapImages: MediaAsset[];
    }
  | {
      kind: "prologue" | "cycle-situation" | "cycle-outcome";
      id: string;
      createdAt: string;
      title: string;
      summary: string;
      body: string;
      images: MediaAsset[];
    };
function CycleFlowGuide({ activePhase }: { activePhase: GameCycle["phase"] }) {
  const activeIndex =
    activePhase === "DAY"
      ? 1
      : activePhase === "DAY_CLOSING"
        ? 2
        : activePhase === "AGENDA"
          ? 3
          : activePhase === "VOTING"
            ? 5
            : activePhase === "PUBLISHED"
              ? 6
              : 0;
  const steps = [
    { title: "Ситуация", text: "прочитать проблему и слабые места города" },
    {
      title: "Действия",
      text: "использовать доступные действия; лимит зависит от должности",
    },
    { title: "Переговоры", text: "обсудить решения с игроками и NPC" },
    {
      title: "Инициативы",
      text: "после действий закрепить результаты проектом или пропустить этап",
    },
    { title: "Готовность", text: "подтвердить готовность к голосованию" },
    { title: "Голосование", text: "поддержать, отклонить или воздержаться" },
    { title: "Итоги", text: "проверить ресурсы, угрозы и летопись" },
  ];
  return (
    <ol className="cc-flow-guide">
      {steps.map((step, index) => (
        <li
          key={step.title}
          className={
            index === activeIndex ? "active" : index < activeIndex ? "done" : ""
          }
        >
          <b>{index + 1}</b>
          <span>
            <strong>{step.title}</strong>
            <small>{step.text}</small>
          </span>
        </li>
      ))}
    </ol>
  );
}
function cycleNextStep(cycle: GameCycle): string {
  if (cycle.phase === "DAY") {
    if (cycle.myDayReady)
      return cycle.actionsUsed < cycle.actionLimit
        ? "вы завершили день вручную; можно вернуться к действиям, пока этап не закрыт для всех."
        : "вы исчерпали действия этого цикла; день закроется автоматически, когда остальные тоже завершат день.";
    if (cycle.canAct)
      return `выполните доступные действия: осталось ${Math.max(0, cycle.actionLimit - cycle.actionsUsed)} из ${cycle.actionLimit}. Инициативы откроются отдельным этапом после действий.`;
    return "лимит действий исчерпан; система автоматически переведёт вас к этапу инициатив после завершения дня всеми игроками.";
  }
  if (cycle.phase === "DAY_CLOSING")
    return "идёт последнее окно NPC-встреч; после него откроется отдельный этап инициатив.";
  if (cycle.phase === "AGENDA")
    return "создайте инициативу из результатов действий или явно пропустите этап, затем подтвердите готовность к голосованию.";
  if (cycle.phase === "VOTING") return "проголосуйте по инициативам Совета.";
  if (cycle.phase === "PUBLISHED")
    return "посмотрите итоги, изменения мира и угрозы; организатор может начать следующий цикл.";
  if (cycle.phase === "RESOLVING")
    return "решения уже считаются; дождитесь публикации итогов.";
  if (cycle.phase === "NARRATING")
    return "механика рассчитана, формируется художественная хроника.";
  return "система готовит новый цикл.";
}
type ChronicleShowcaseItem = {
  id: string;
  seenKey: string;
  meta: string;
  shortLabel: string;
  title: string;
  summaryText: string;
  situationHeading: string;
  situationText: string;
  outcomeHeading: string;
  outcomeText: string;
  images: MediaAsset[];
  changes: WorldChange[];
  feedItem: ChronicleFeedItem;
};
function buildChronicleShowcaseItems(setup: CampaignSetup, cycle: GameCycle): ChronicleShowcaseItem[] {
  const items: ChronicleShowcaseItem[] = [];
  if (setup.world) {
    const prologueFeed: ChronicleFeedItem = {
      kind: "prologue",
      id: `world-prologue-${setup.world.id}`,
      createdAt: setup.world.createdAt,
      title: `Пролог: ${setup.world.worldName}`,
      summary: setup.world.summary,
      body: setup.world.openingNarrative || setup.world.summary,
      images: setup.world.openingImages,
    };
    items.push({
      id: prologueFeed.id,
      seenKey: `prologue:${setup.world.id}`,
      meta: "Пролог",
      shortLabel: "Вступление",
      title: setup.world.worldName,
      summaryText: compactText(setup.world.summary, 220),
      situationHeading: "Вхождение в игру",
      situationText: setup.world.openingNarrative || setup.world.summary,
      outcomeHeading: "О мире",
      outcomeText: setup.world.summary,
      images: setup.world.openingImages,
      changes: [],
      feedItem: prologueFeed,
    });
  }

  const chapters = [...setup.cycle.chronicleChapters].sort((left, right) => left.cycleNumber - right.cycleNumber);
  chapters.forEach((chapter) => {
    const chapterFeed: ChronicleFeedItem = {
      kind: "chapter",
      id: `chapter-${chapter.id}`,
      createdAt: chapter.createdAt,
      chapter,
      recapImages: chapter.recapImages ?? [],
    };
    items.push({
      id: chapterFeed.id,
      seenKey: `chapter:${chapter.id}`,
      meta: `Глава ${chapter.cycleNumber}`,
      shortLabel: `День ${chapter.cycleNumber}`,
      title: chapter.title,
      summaryText: compactText(chapter.closingNarrative || chapter.consequencesText || chapter.openingSummary, 220),
      situationHeading: `Ситуация дня ${chapter.cycleNumber}`,
      situationText: chapter.openingSummary || "Подробная ситуация не сохранена в отдельном блоке.",
      outcomeHeading: `Итог дня ${chapter.cycleNumber}`,
      outcomeText: chapter.closingNarrative || chapter.consequencesText || "Итог главы ещё формируется.",
      images: chapter.recapImages ?? [],
      changes: chapter.worldChanges ?? [],
      feedItem: chapterFeed,
    });
  });

  if (items.length === 0 && (cycle.openingImages.length > 0 || cycle.situationSummary || cycle.closingNarrative)) {
    const synthetic: ChronicleFeedItem = {
      kind: "cycle-outcome",
      id: `cycle-outcome-${cycle.id}`,
      createdAt: cycle.openedAt || new Date(0).toISOString(),
      title: `Цикл ${cycle.cycleNumber}`,
      summary: cycle.closingNarrative || cycle.situationSummary || cycle.title,
      body: cycle.closingNarrative || cycle.situationSummary || cycle.title,
      images: cycle.chronicleComicRecapImages.length > 0 ? cycle.chronicleComicRecapImages : cycle.openingImages,
    };
    items.push({
      id: synthetic.id,
      seenKey: `cycle:${cycle.id}`,
      meta: `Цикл ${cycle.cycleNumber}`,
      shortLabel: `Цикл ${cycle.cycleNumber}`,
      title: cycle.title,
      summaryText: compactText(cycle.closingNarrative || cycle.situationSummary || cycle.title, 220),
      situationHeading: "Ситуация дня",
      situationText: cycle.situationSummary || cycle.centralProblem || cycle.title,
      outcomeHeading: "Краткий итог",
      outcomeText: cycle.closingNarrative || cycle.situationSummary || cycle.title,
      images: cycle.chronicleComicRecapImages.length > 0 ? cycle.chronicleComicRecapImages : cycle.openingImages,
      changes: cycle.worldChanges,
      feedItem: synthetic,
    });
  }

  return items;
}
function actionNarrativeStatusText(
  status: ActionNarrativeStatus,
  nextAttemptAt?: string,
): string {
  if (status === "RETRY") {
    const next = nextAttemptAt
      ? ` Следующая попытка — ${formatTime(nextAttemptAt)}.`
      : "";
    return `Сервис временно не вернул корректный результат. Запрос остаётся в очереди и повторяется с увеличенным интервалом.${next}`;
  }
  if (status === "PROCESSING")
    return "Связный результат действия сейчас формируется.";
  return status === "PENDING"
    ? "Действие принято. Результат ожидает своей очереди."
    : "Результат готов.";
}

export {
  compactChangeLabel,
  MockupSectionTitle,
  CycleFlowGuide,
  cycleNextStep,
  buildChronicleShowcaseItems,
  actionNarrativeStatusText,
  GAME_UI_ASSET_BASE,
  KNOWN_CYCLE_SECTIONS,
  isKnownCycleSection,
  OwnerForcePhaseControl,
  forcePhaseHelp,
  GameAssetIcon,
  compactText,
  PlayerIdentity,
  Avatar,
  CycleImages,
  CycleImage,
  DeadlineBadge,
  DeadlineValue,
  formatDuration,
  SectionTitle,
  genericUiIconSrc,
  previewText,
  Paragraphs,
  npcVisitDeadlinePassed,
  dayProgress,
  sameIds,
  readDraft,
  writeDraft,
  clearDraft,
  phaseInfo,
  riskLabel,
  factionStanceLabel,
  statusLabel,
  fundingLabel,
  formatDateTime,
  voteLabel,
  formatTime,
  initials,
};
export type { ActionWorkspaceTab, ChronicleFeedItem, ChronicleShowcaseItem, Props, Section };
